import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Chip,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import Activity from "../../components/Activity";
import { useState, useEffect } from "react";
import { useLoginAnalytics } from "../../features/users/useUsers";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  Cell,
} from "recharts";
import LoginAnalyticsDetails from "../../components/LoginAnalyticsDetails";

const semanticColors = {
  logins: "#ed6c02",
  hourly: "#6d28d9",
  methods: {
    password: "#1976d2",
    google: "#4285f4",
    facebook: "#1877f2",
    code: "#2e7d32",
    unknown: "#9e9e9e",
  },
  apps: "#2e7d32",
  countries: "#0288d1",
  cities: "#d32f2f",
  users: "#7c3aed",
  success: "#4caf50",
  warning: "#ff9800",
  error: "#f44336",
};

export default function LoginAnalytics() {
  const theme = useTheme();

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(null); // Default to "All Years"
  const [selectedMonth, setSelectedMonth] = useState(null);
  const { data: analytics, isLoading } = useLoginAnalytics(
    selectedMonth,
    selectedYear === "all" ? null : selectedYear
  );

  const yearsAvailable = Array.from(
    new Set((analytics?.series?.createdYearly || []).map((d) => d.year))
  );

  // Update selected year when years become available (only if a specific year was selected)
  useEffect(() => {
    if (
      selectedYear !== null &&
      yearsAvailable.length > 0 &&
      !yearsAvailable.includes(selectedYear)
    ) {
      const newYear = yearsAvailable.includes(currentYear)
        ? currentYear
        : yearsAvailable[yearsAvailable.length - 1];
      setSelectedYear(newYear);
    }
  }, [yearsAvailable, selectedYear, currentYear]);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsFilters, setDetailsFilters] = useState({});

  const handleChartClick = (data, type) => {
    if (!data || !data.activePayload || !data.activePayload.length) return;
    const payload = data.activePayload[0].payload;

    let value = "";
    let filterType = type;

    if (
      type === "login_method" ||
      type === "login_app" ||
      type === "country" ||
      type === "city"
    ) {
      if (type === "login_method") {
        const methodMap = {
          Password: "password",
          Google: "google",
          Facebook: "facebook",
          "Code / OTP": "code",
          Unknown: "unknown",
        };
        value = methodMap[payload.name] || payload.name.toLowerCase();
      } else {
        value = payload.name;
      }
    } else if (type === "user") {
      value = payload.email;
    } else if (type === "year") {
      value = payload.year;
    } else if (type === "monthly_daily") {
      if (selectedMonth !== null) {
        // Daily view
        filterType = "day";
        // Construct date string YYYY-MM-DD
        const day = String(payload.day).padStart(2, "0");
        const month = String(selectedMonth + 1).padStart(2, "0"); // selectedMonth is 0-index
        value = `${selectedYear}-${month}-${day}`;
      } else {
        // Monthly view
        filterType = "month_label";
        value = payload.month; // "Jan", "Feb" etc
      }
    }

    setDetailsFilters({
      type: filterType,
      value: value,
      year: selectedYear === null ? "all" : selectedYear,
      month: selectedMonth, // Pass context
    });
    setDetailsOpen(true);
  };

  const LoadingSkeleton = () => (
    <Box sx={{ mb: 3 }}>
      {/* Loading Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 4,
        }}
      >
        <CircularProgress
          size={24}
          sx={{ mr: 2, color: theme.palette.primary.main }}
        />
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          Loading login analytics data...
        </Typography>
      </Box>

      {/* Stat Cards Skeleton */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper sx={{ p: 2, borderRadius: 2, opacity: 0.8 }}>
              <Skeleton
                variant="text"
                width="60%"
                height={24}
                sx={{ bgcolor: "grey.300" }}
              />
              <Skeleton
                variant="rectangular"
                height={40}
                sx={{ mt: 1, borderRadius: 1, bgcolor: "grey.300" }}
              />
              <Box sx={{ mt: 1 }}>
                <Skeleton
                  variant="text"
                  width="80%"
                  sx={{ bgcolor: "grey.300" }}
                />
                <Skeleton
                  variant="text"
                  width="70%"
                  sx={{ bgcolor: "grey.300" }}
                />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Charts Skeleton */}
      <Grid container spacing={2}>
        {/* Yearly Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: 2, height: 280, opacity: 0.8 }}>
            <Skeleton
              variant="text"
              width="40%"
              height={32}
              sx={{ mb: 2, bgcolor: "grey.300" }}
            />
            <Skeleton
              variant="rectangular"
              height={220}
              sx={{ borderRadius: 1, bgcolor: "grey.300" }}
            />
          </Paper>
        </Grid>

        {/* Login Methods and Top Apps */}
        {[1, 2].map((i) => (
          <Grid item xs={12} md={6} key={i}>
            <Paper sx={{ p: 2, borderRadius: 2, height: 280, opacity: 0.8 }}>
              <Skeleton
                variant="text"
                width="40%"
                height={32}
                sx={{ mb: 2, bgcolor: "grey.300" }}
              />
              <Skeleton
                variant="rectangular"
                height={220}
                sx={{ borderRadius: 1, bgcolor: "grey.300" }}
              />
            </Paper>
          </Grid>
        ))}

        {/* Top Countries, Cities, Users */}
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} md={6} key={i}>
            <Paper sx={{ p: 2, borderRadius: 2, height: 280, opacity: 0.8 }}>
              <Skeleton
                variant="text"
                width="40%"
                height={32}
                sx={{ mb: 2, bgcolor: "grey.300" }}
              />
              <Skeleton
                variant="rectangular"
                height={220}
                sx={{ borderRadius: 1, bgcolor: "grey.300" }}
              />
            </Paper>
          </Grid>
        ))}

        {/* Year Selector and Monthly Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, height: 280, opacity: 0.8 }}>
            <Skeleton
              variant="text"
              width="40%"
              height={32}
              sx={{ mb: 2, bgcolor: "grey.300" }}
            />
            <Skeleton
              variant="rectangular"
              height={220}
              sx={{ borderRadius: 1, bgcolor: "grey.300" }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: 2, height: 280, opacity: 0.8 }}>
            <Skeleton
              variant="text"
              width="40%"
              height={32}
              sx={{ mb: 2, bgcolor: "grey.300" }}
            />
            <Skeleton
              variant="rectangular"
              height={220}
              sx={{ borderRadius: 1, bgcolor: "grey.300" }}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <>
      <Activity />
      <Box
        sx={{
          color: theme.palette.text.primary,
          p: 3,
          minHeight: "100vh",
          boxShadow: "none",
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(2),
          borderRadius: 2,
        }}
      >
        <Container maxWidth="">
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            analytics && (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    ml: 1,
                    mt: 1,
                  }}
                >
                  <Typography variant="h4" fontWeight="bold">
                    Login Analytics
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Year</InputLabel>
                      <Select
                        value={selectedYear === null ? "all" : selectedYear}
                        label="Year"
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedYear(val === "all" ? null : Number(val));
                        }}
                      >
                        <MenuItem value="all">All Years</MenuItem>
                        {yearsAvailable.length > 0 ? (
                          yearsAvailable.map((y) => (
                            <MenuItem key={y} value={y}>
                              {y}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value={currentYear}>{currentYear}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Month</InputLabel>
                      <Select
                        value={selectedMonth === null ? "all" : selectedMonth}
                        label="Month"
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedMonth(val === "all" ? null : Number(val));
                        }}
                      >
                        <MenuItem value="all">All Months</MenuItem>
                        {months.map((m, idx) => (
                          <MenuItem key={idx} value={idx}>
                            {m}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          boxShadow: "none",
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography variant="subtitle2">
                          Total Sessions
                        </Typography>
                        <Typography variant="h5">{analytics.total}</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Active Sessions
                      </Typography>
                      <Typography variant="h5">{analytics.sessions?.active || 0}</Typography>
                    </Box>
                  </Grid> */}
                  {/* 
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Expired Sessions
                      </Typography>
                      <Typography variant="h5">{analytics.sessions?.expired || 0}</Typography>
                    </Box>
                  </Grid> */}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Login Methods</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            onClick={(data) =>
                              handleChartClick(data, "login_method")
                            }
                            cursor="pointer"
                            data={[
                              {
                                name: "Password",
                                value: analytics.loginMethod?.password || 0,
                              },
                              {
                                name: "Google",
                                value: analytics.loginMethod?.google || 0,
                              },
                              {
                                name: "Facebook",
                                value: analytics.loginMethod?.facebook || 0,
                              },
                              {
                                name: "Code / OTP",
                                value: analytics.loginMethod?.code || 0,
                              },
                              {
                                name: "Unknown",
                                value: analytics.loginMethod?.unknown || 0,
                              },
                            ]}
                          >
                            <XAxis dataKey="name" />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar dataKey="value" name="Count">
                              {[
                                semanticColors.methods.password,
                                semanticColors.methods.google,
                                semanticColors.methods.facebook,
                                semanticColors.methods.code,
                                semanticColors.methods.unknown,
                              ].map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Top Apps</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            onClick={(data) =>
                              handleChartClick(data, "login_app")
                            }
                            cursor="pointer"
                            data={(analytics.loginApp || []).slice(0, 10)}
                          >
                            <XAxis
                              dataKey="name"
                              interval={0}
                              angle={-20}
                              height={60}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar
                              dataKey="count"
                              fill={semanticColors.apps}
                              name="Count"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Top Countries</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            onClick={(data) =>
                              handleChartClick(data, "country")
                            }
                            cursor="pointer"
                            data={analytics.topCountries || []}
                          >
                            <XAxis
                              dataKey="name"
                              interval={0}
                              angle={-20}
                              height={60}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar
                              dataKey="count"
                              fill={semanticColors.countries}
                              name="Logins"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Top Cities</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            onClick={(data) => handleChartClick(data, "city")}
                            cursor="pointer"
                            data={analytics.topCities || []}
                          >
                            <XAxis
                              dataKey="name"
                              interval={0}
                              angle={-20}
                              height={60}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar
                              dataKey="count"
                              fill={semanticColors.cities}
                              name="Logins"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Top Users */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Top Users</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            onClick={(data) => handleChartClick(data, "user")}
                            cursor="pointer"
                            data={analytics.topUsers || []}
                          >
                            <XAxis
                              dataKey="email"
                              interval={0}
                              angle={-20}
                              height={60}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar
                              dataKey="count"
                              fill={semanticColors.users}
                              name="Logins"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Logins by Year
                      </Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            onClick={(data) => handleChartClick(data, "year")}
                            cursor="pointer"
                            data={(analytics?.series?.createdYearly || []).map(
                              (d) => ({
                                year: d.year,
                                count: d.count,
                              })
                            )}
                          >
                            <XAxis dataKey="year" />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar
                              dataKey="count"
                              fill={semanticColors.logins}
                              name="Logins"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Monthly/Daily Logins Chart */}
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">
                        {selectedMonth !== null
                          ? `Daily Logins - ${months[selectedMonth]} ${selectedYear}`
                          : selectedYear !== null
                          ? `Monthly Logins - ${selectedYear}`
                          : `Monthly Logins - All Years`}
                      </Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            onClick={(data) =>
                              handleChartClick(data, "monthly_daily")
                            }
                            cursor="pointer"
                            data={
                              selectedMonth !== null
                                ? analytics?.series?.createdDaily || []
                                : selectedYear !== null
                                ? months.map((m, idx) => ({
                                    month: m.slice(0, 3), // Use 3-letter abbreviation for chart
                                    count:
                                      analytics?.series?.createdMonthly?.find(
                                        (d) =>
                                          d.year === selectedYear &&
                                          d.month === idx + 1
                                      )?.count || 0,
                                  }))
                                : months.map((m, idx) => {
                                    // Aggregate data across all years for each month
                                    const monthData =
                                      analytics?.series?.createdMonthly?.filter(
                                        (d) => d.month === idx + 1
                                      ) || [];
                                    const totalCount = monthData.reduce(
                                      (sum, item) => sum + item.count,
                                      0
                                    );
                                    return {
                                      month: m.slice(0, 3), // Use 3-letter abbreviation for chart
                                      count: totalCount,
                                    };
                                  })
                            }
                          >
                            <XAxis
                              dataKey={selectedMonth !== null ? "day" : "month"}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar
                              dataKey="count"
                              fill={semanticColors.logins}
                              name={
                                selectedMonth !== null
                                  ? "Daily Logins"
                                  : "Monthly Logins"
                              }
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )
          )}
        </Container>
      </Box>
      <LoginAnalyticsDetails
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        filters={detailsFilters}
      />
    </>
  );
}
